import type { ManualChapter } from './types'

export const gettingStartedChapter: ManualChapter = {
  id: 'getting-started',
  number: 2,
  title: 'Primi Passi',
  description: 'Come accedere e configurare il proprio account',
  icon: 'RocketLaunchIcon',
  sections: [
    {
      id: 'login',
      title: 'Accesso alla Piattaforma',
      requirement: '§2.1 Area Riservata e Ruoli',
      content: `
## Accesso alla Piattaforma

Per accedere alla piattaforma {{APP_NAME}} Community, segui questi passaggi:

### 1. Raggiungi la Pagina di Login

Apri il browser e naviga all'indirizzo della piattaforma. Ti troverai di fronte alla schermata di accesso.

![Pagina di Login](login.png)
*Schermata di login della piattaforma*

### 2. Inserisci le Credenziali

- **Email**: Inserisci l'indirizzo email con cui ti sei registrato
- **Password**: Inserisci la tua password

### 3. Opzioni Aggiuntive

- **Ricordami**: Seleziona questa opzione per rimanere connesso su questo dispositivo
- **Password dimenticata?**: Clicca se hai bisogno di recuperare la password

### 4. Accedi

Clicca sul pulsante **"Accedi"** per entrare nella piattaforma.

> **Importante**: Dopo diversi tentativi falliti, l'account potrebbe essere temporaneamente bloccato per sicurezza.
`,
      screenshots: [
        {
          id: 'login',
          path: '/assets/manual/login.png',
          caption: 'Pagina di login della piattaforma',
          alt: 'Schermata di login con campi email e password'
        }
      ]
    },
    {
      id: 'register',
      title: 'Registrazione',
      requirement: '§2.1 Registrazione utenti con approvazione Admin',
      content: `
## Registrazione

Se non hai ancora un account, puoi registrarti seguendo questa procedura:

### 1. Accedi alla Pagina di Registrazione

Dalla pagina di login, clicca su **"Registrati"** per aprire il form di registrazione.

![Pagina di Registrazione](register.png)
*Form di registrazione nuovo utente*

### 2. Compila il Form

Inserisci tutti i dati richiesti:

| Campo | Descrizione | Obbligatorio |
|-------|-------------|--------------|
| **Nome** | Il tuo nome | Sì |
| **Cognome** | Il tuo cognome | Sì |
| **Email** | Email aziendale valida | Sì |
| **Password** | Minimo 8 caratteri | Sì |
| **Conferma Password** | Ripeti la password | Sì |
| **Azienda** | Nome della tua azienda | No |
| **Ruolo** | La tua posizione lavorativa | No |

### 3. Accetta Privacy e Termini

Prima di procedere, è necessario:
- ✅ Leggere e accettare l'**Informativa sulla Privacy**
- ✅ Accettare i **Termini e Condizioni** di utilizzo

### 4. Invia la Richiesta

Clicca su **"Registrati"** per inviare la tua richiesta.

### 5. Attendi l'Approvazione

La tua richiesta verrà valutata da un amministratore.

> **Nota**: Riceverai un'email di conferma quando il tuo account sarà approvato.

### Stati della Registrazione

| Stato | Descrizione |
|-------|-------------|
| **In attesa** | Registrazione inviata, in attesa di approvazione |
| **Approvato** | Account attivo, puoi effettuare il login |
| **Rifiutato** | Richiesta non approvata (riceverai motivazione via email) |
`,
      screenshots: [
        {
          id: 'register',
          path: '/assets/manual/register.png',
          caption: 'Form di registrazione',
          alt: 'Form di registrazione con campi nome, cognome, email e password'
        }
      ]
    },
    {
      id: 'password-recovery',
      title: 'Recupero Password',
      content: `
## Recupero Password

Se hai dimenticato la password, puoi recuperarla facilmente:

### Procedura

1. **Vai alla pagina di login**
2. **Clicca su "Password dimenticata?"**
3. **Inserisci la tua email** registrata sulla piattaforma
4. **Controlla la casella email** (anche la cartella spam)
5. **Clicca sul link** contenuto nell'email
6. **Imposta una nuova password** (minimo 8 caratteri)
7. **Conferma** e accedi con le nuove credenziali

### Suggerimenti per la Password

Una password sicura dovrebbe contenere:
- ✅ Almeno 8 caratteri
- ✅ Lettere maiuscole e minuscole
- ✅ Almeno un numero
- ✅ Almeno un carattere speciale (!@#$%...)

> **Sicurezza**: Il link di reset scade dopo 24 ore. Se non lo usi in tempo, richiedi un nuovo reset.
`
    },
    {
      id: '2fa',
      title: 'Autenticazione a Due Fattori (2FA)',
      requirement: '§2.1 Autenticazione a due fattori (2FA)',
      content: `
## Autenticazione a Due Fattori (2FA)

L'autenticazione a due fattori aggiunge un livello extra di sicurezza al tuo account.

### Cos'è la 2FA?

La 2FA richiede **due elementi** per accedere:
1. **Qualcosa che sai**: la tua password
2. **Qualcosa che hai**: il tuo smartphone con l'app autenticatore

Anche se qualcuno scoprisse la tua password, non potrebbe accedere senza il codice generato dal tuo telefono.

### Come Attivarla

#### 1. Accedi al Profilo
Vai su **Profilo** → **Sicurezza** → **Autenticazione a due fattori**

#### 2. Inizia la Configurazione
Clicca su **"Attiva 2FA"**

#### 3. Scarica un'App Autenticatore
Se non ne hai già una, scarica una di queste app gratuite:

| App | Disponibile per |
|-----|-----------------|
| **Google Authenticator** | iOS, Android |
| **Microsoft Authenticator** | iOS, Android |
| **Authy** | iOS, Android, Desktop |

#### 4. Scansiona il QR Code
Apri l'app e scansiona il codice QR mostrato a schermo.

#### 5. Inserisci il Codice di Verifica
L'app genererà un codice a 6 cifre. Inseriscilo per verificare che tutto funzioni.

#### 6. Salva i Codici di Backup
**Importante**: Salva i codici di backup in un luogo sicuro. Ti serviranno se perdi l'accesso al telefono.

### Accesso con 2FA

Dopo aver attivato la 2FA, quando fai login:
1. Inserisci email e password come sempre
2. Ti verrà chiesto il **codice a 6 cifre**
3. Apri l'app autenticatore e inserisci il codice corrente
4. Il codice cambia ogni **30 secondi**

### Disattivare la 2FA

Se necessario, puoi disattivare la 2FA da:
**Profilo** → **Sicurezza** → **Disattiva 2FA**

> **Attenzione**: Disattivare la 2FA riduce la sicurezza del tuo account.
`
    },
    {
      id: 'profile',
      title: 'Il Tuo Profilo',
      requirement: '§2.1 Gestione profilo e preferenze',
      content: `
## Il Tuo Profilo

La pagina profilo ti permette di gestire i tuoi dati personali e le preferenze.

![Pagina Profilo](profile.png)
*Pagina del profilo utente*

### Accedere al Profilo

Clicca sul tuo nome/avatar in alto a destra e seleziona **"Profilo"**.

### Informazioni Personali

Puoi modificare i seguenti dati:

| Campo | Descrizione |
|-------|-------------|
| **Nome e Cognome** | Come appari agli altri utenti |
| **Avatar** | Immagine del profilo (max 2MB) |
| **Bio** | Breve descrizione di te stesso |
| **Azienda** | Nome della tua organizzazione |
| **Ruolo** | La tua posizione lavorativa |
| **Telefono** | Numero di contatto (opzionale) |
| **LinkedIn** | Link al tuo profilo LinkedIn |

### Come Cambiare Avatar

1. Clicca sull'avatar attuale o sul pulsante **"Cambia foto"**
2. Seleziona un'immagine dal tuo dispositivo
3. L'immagine verrà caricata automaticamente

**Formati supportati**: JPG, PNG, GIF (max 2MB)

### Preferenze Notifiche

Puoi personalizzare quali notifiche ricevere:

- ✅ Nuove risposte alle tue discussioni
- ✅ Menzioni (@tuonome)
- ✅ Nuovi corsi disponibili
- ✅ Eventi in programma
- ✅ Aggiornamenti Task Force
- ✅ Risposte Compliance Clinic
- ✅ Newsletter

### Statistiche Personali

Nel profilo puoi vedere:
- **Punti totali** accumulati
- **Livello** raggiunto
- **Badge** ottenuti
- **Corsi** completati
- **Post** nel forum
- **Certificati** conseguiti

### Cambiare Password

1. Vai su **Profilo** → **Sicurezza**
2. Clicca su **"Cambia Password"**
3. Inserisci la password attuale
4. Inserisci la nuova password (2 volte)
5. Clicca **"Salva"**
`,
      screenshots: [
        {
          id: 'profile',
          path: '/assets/manual/profile.png',
          caption: 'Pagina del profilo utente',
          alt: 'Pagina profilo con informazioni personali e statistiche'
        }
      ]
    }
  ]
}
